/**
 * NewToolsFieldWithMic – Rich text editor with toolbar, mic, image upload/capture, Save/Complete/Dispatch.
 * Matches admin4.0 CommonComponents NewToolsFieldWithMic for Histopathology report.
 */
import React, { useRef, useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
  Grid,
} from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import TableChartIcon from "@mui/icons-material/TableChart";
import MicIcon from "@mui/icons-material/Mic";
import VoiceToText from "./VoiceToText";
import OpenWebCamPopUP from "./OpenWebCamPopUP";
import { ImagePaths } from "../../Utility/ImagePaths";

function removeEmptyStyleTagsAndReplaceSpan(content) {
  if (!content || content === "<br>") return "";
  const div = document.createElement("div");
  div.innerHTML = content;
  const elements = div.querySelectorAll("*[style]");
  elements.forEach((element) => {
    const style = element.getAttribute("style");
    const retainedStyles = (style || "")
      .split(";")
      .map((r) => r.trim())
      .filter((r) => {
        const [property, value] = r.split(":").map((s) => s?.trim());
        return property === "text-align" && ["center", "left", "right"].includes(value);
      })
      .join("; ");
    if (retainedStyles) element.setAttribute("style", retainedStyles);
    else element.removeAttribute("style");
  });
  div.querySelectorAll("span").forEach((span) => {
    const newDiv = document.createElement("div");
    Array.from(span.attributes).forEach((attr) => newDiv.setAttribute(attr.name, attr.value));
    while (span.firstChild) newDiv.appendChild(span.firstChild);
    span.parentNode.replaceChild(newDiv, span);
  });
  const tables = div.querySelectorAll("table");
  tables.forEach((table) => {
    table.setAttribute("style", "border-collapse: collapse; width: 100%; margin: 10px 0; border: 1px solid #ccc;");
    table.querySelectorAll("td").forEach((td) => {
      td.setAttribute("style", "border: 1px solid #ccc; padding: 8px; vertical-align: top;");
    });
  });
  const firstDiv = div.firstElementChild;
  if (firstDiv && firstDiv.tagName.toLowerCase() === "div") {
    const style = firstDiv.getAttribute("style");
    if (style && style.includes("text-align")) {
      const alignValue = style.match(/text-align:\s*(left|right|center)/);
      firstDiv.setAttribute("style", alignValue ? alignValue[0] : "text-align: left;");
    } else {
      firstDiv.setAttribute("style", "text-align: left;");
    }
  }
  return div.innerHTML;
}

const captureImgSrc = ImagePaths?.captureImg?.default ?? ImagePaths?.captureImg;
const captureImgOffSrc = ImagePaths?.captureImgOff?.default ?? ImagePaths?.captureImgOff;

export default function NewToolsFieldWithMic({
  FromTestReport = false,
  selectedServiceData,
  EditorDisable = false,
  isDispatchshown = true,
  postButtonLabel = "Send Approval",
  showOnlyApproveButton = false,
  viewOnly = false,
  Title = "Result",
  EditorRef = "",
  handleSaveResult,
  handlePostResult,
  handleDispatchResult,
  overallHeight = "78vh",
  height = "51.5vh",
  EditorHeight = "53vh",
  ResultPDF = [],
  micTextEditDisabled = false,
  addSpaceOnTab = true,
  errorMsg,
}) {
  const editorRef = useRef(null);
  const uploadPDFRef = useRef(null);
  const clickKeyRef = useRef(false);

  const status = selectedServiceData?.result_status ?? "";
  const isCompleted = status === "Completed";

  const [bold, setBold] = useState(false);
  const [italic, setItalic] = useState(false);
  const [underline, setUnderline] = useState(false);
  const [justifyLeft, setJustifyLeft] = useState(true);
  const [justifyCenter, setJustifyCenter] = useState(false);
  const [justifyRight, setJustifyRight] = useState(false);
  const [bulletList, setBulletList] = useState(false);
  const [orderedList, setOrderedList] = useState(false);
  const [tableMenuAnchor, setTableMenuAnchor] = useState(null);
  const [tableRows, setTableRows] = useState(3);
  const [tableCols, setTableCols] = useState(3);
  const [listenPopUp, setListenPopUp] = useState(false);
  const [pdfUploadList, setPdfUploadList] = useState(() => (Array.isArray(ResultPDF) ? [...ResultPDF] : []));
  const [capturedImg, setCapturedImg] = useState(null);
  const [isOutFocus, setIsOutFocus] = useState(false);
  const [contentVersion, setContentVersion] = useState(0);
  const [imgCapClked, setImgCapClked] = useState(false);

  useEffect(() => {
    if (Array.isArray(ResultPDF) && ResultPDF.length >= 0) {
      setPdfUploadList((prev) => (prev.length === 0 && ResultPDF.length > 0 ? [...ResultPDF] : prev));
    }
  }, [ResultPDF?.length]);

  useEffect(() => {
    if (!editorRef.current) return;
    const next = EditorRef ?? "";
    if (editorRef.current.innerHTML !== next) {
      editorRef.current.innerHTML = next;
    }
  }, [EditorRef]);

  useEffect(() => {
    if (!listenPopUp && editorRef.current) editorRef.current.focus();
  }, [listenPopUp]);

  const listenPopUpClose = useCallback(() => {
    clickKeyRef.current = false;
    setListenPopUp(false);
  }, []);

  const voiceToTextgetter = useCallback((textStorage) => {
    if (!textStorage || !clickKeyRef.current) return;
    setTimeout(() => {
      const editor = editorRef.current;
      if (!editor) return;
      editor.focus();
      const textToInsert = ` ${(textStorage || "").trim()}`;
      document.execCommand("insertText", false, textToInsert);
    }, 0);
  }, []);

  const handleStyleChange = (command) => {
    document.execCommand(command, false, null);
    editorRef.current?.focus();
    switch (command) {
      case "bold": setBold((b) => !b); break;
      case "italic": setItalic((i) => !i); break;
      case "underline": setUnderline((u) => !u); break;
      case "insertUnorderedList":
        setBulletList((b) => !b);
        setOrderedList(false);
        break;
      case "insertOrderedList":
        setOrderedList((o) => !o);
        setBulletList(false);
        break;
      case "justifyLeft":
        setJustifyLeft(true); setJustifyCenter(false); setJustifyRight(false);
        break;
      case "justifyCenter":
        setJustifyLeft(false); setJustifyCenter(true); setJustifyRight(false);
        break;
      case "justifyRight":
        setJustifyLeft(false); setJustifyCenter(false); setJustifyRight(true);
        break;
      default:
        break;
    }
  };

  const getTextStylesAtCursor = useCallback(() => {
    setBold(false); setItalic(false); setUnderline(false);
    setJustifyLeft(false); setJustifyCenter(false); setJustifyRight(false);
    setBulletList(false); setOrderedList(false);
    const selection = window.getSelection();
    if (!selection?.rangeCount) return;
    const range = selection.getRangeAt(0);
    const node = range.commonAncestorContainer;
    let current = node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
    while (current && current !== editorRef.current) {
      if (current.tagName === "UL") { setBulletList(true); break; }
      if (current.tagName === "OL") { setOrderedList(true); break; }
      current = current.parentElement;
    }
    const el = node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
    const style = el && window.getComputedStyle(el);
    if (!style) return;
    if (style.fontStyle === "italic" || document.queryCommandState("italic")) setItalic(true);
    if (style.fontWeight === "700" || document.queryCommandState("bold")) setBold(true);
    if (style.textDecoration?.includes("underline") || document.queryCommandState("underline")) setUnderline(true);
    if (style.textAlign === "left") setJustifyLeft(true);
    if (style.textAlign === "center") setJustifyCenter(true);
    if (style.textAlign === "right") setJustifyRight(true);
  }, []);

  const handlePaste = (e) => {
    e.preventDefault();
    const plainText = e.clipboardData.getData("text/plain");
    const normalized = plainText.normalize("NFKD").replace(/[\u0300-\u036f]/g, "");
    document.execCommand("insertText", false, normalized);
  };

  const handleKeyDown = (e) => {
    if (addSpaceOnTab && e.key === "Tab") {
      e.preventDefault();
      document.execCommand("insertHTML", false, "\u00A0\u00A0\u00A0\u00A0\u00A0");
    }
  };

  const generateTableHTML = (rows, cols) => {
    let html = '<table style="border-collapse: collapse; width: 100%; margin: 10px 0; border: 1px solid #ccc;">';
    for (let i = 0; i < rows; i++) {
      html += "<tr>";
      for (let j = 0; j < cols; j++) {
        html += '<td style="border: 1px solid #ccc; padding: 8px; min-width: 60px; vertical-align: top;">&nbsp;</td>';
      }
      html += "</tr>";
    }
    html += "</table>";
    return html;
  };

  const handleTableInsert = () => {
    document.execCommand("insertHTML", false, generateTableHTML(tableRows, tableCols));
    editorRef.current?.focus();
    setTableMenuAnchor(null);
  };

  const onUploadPDF = (event) => {
    const fileList = Array.from(event.target.files || []);
    if (!fileList.length) {
      errorMsg?.("No file selected.");
      return;
    }
    const allowed = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/bmp", "image/webp"];
    const maxSize = 3 * 1024 * 1024;
    const finallist = [];
    let processed = 0;
    fileList.forEach((file) => {
      if (!allowed.includes(file.type)) {
        errorMsg?.("Only image files are allowed (JPEG, JPG, PNG, GIF, BMP, WEBP).");
        processed++;
        if (processed === fileList.length && finallist.length > 0) {
          setPdfUploadList((prev) => [...prev, ...finallist]);
          if (uploadPDFRef.current) uploadPDFRef.current.value = "";
        }
        return;
      }
      if (file.size > maxSize) {
        errorMsg?.("File size exceeds 3 MB.");
        processed++;
        if (processed === fileList.length && finallist.length > 0) {
          setPdfUploadList((prev) => [...prev, ...finallist]);
          if (uploadPDFRef.current) uploadPDFRef.current.value = "";
        }
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        const blob = new Blob([e.target.result], { type: file.type });
        finallist.push({ document_file: blob, document_name: file.name });
        processed++;
        if (processed === fileList.length) {
          setPdfUploadList((prev) => [...prev, ...finallist]);
          if (uploadPDFRef.current) uploadPDFRef.current.value = "";
        }
      };
      reader.onerror = () => {
        errorMsg?.("Error reading the file.");
        processed++;
        if (processed === fileList.length && finallist.length > 0) {
          setPdfUploadList((prev) => [...prev, ...finallist]);
          if (uploadPDFRef.current) uploadPDFRef.current.value = "";
        }
      };
      reader.readAsArrayBuffer(file);
    });
  };

  const closeWebCamPop = () => setImgCapClked(false);

  const addImgCapture = (data) => {
    if (data) {
      setCapturedImg(data);
      setImgCapClked(false);
    }
  };

  const disableBtn = isCompleted || !!capturedImg;
  const hasContent = editorRef.current?.innerHTML && editorRef.current?.innerHTML !== "<br>";
  const canComplete = !isCompleted && (hasContent || pdfUploadList.length > 0 || !!capturedImg);

  const triggerContentCheck = () => setContentVersion((v) => v + 1);

  return (
    <Paper
      component="div"
      className="rad_tst_tmp_content"
      elevation={1}
      sx={{
        height: overallHeight,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        minHeight: 0,
      }}
    >
      <Box
        component="div"
        borderBottom="1px solid lightgray"
        pl="0.5vw"
        sx={{ height: "3.5vw", minHeight: 40, flexShrink: 0 }}
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="flex-start"
        onMouseLeave={() => setIsOutFocus(true)}
        onMouseOver={() => setIsOutFocus(false)}
      >
        <Box display="flex" justifyContent="flex-start" alignItems="center" width="100%">
          <Typography fontWeight={600}>{Title}</Typography>
          <Box className="toolbar" display="flex" flexDirection="row" alignItems="center" sx={{ cursor: "pointer", ml: 1 }} onMouseLeave={() => setIsOutFocus(true)} onMouseOver={() => setIsOutFocus(false)}>
            <ButtonGroup variant="text" aria-label="formatting" sx={{ visibility: EditorDisable ? "hidden" : "visible" }}>
              <Tooltip title="Bold" placement="top" arrow>
                <IconButton disabled={EditorDisable} onClick={() => handleStyleChange("bold")} size="small">
                  <FormatBoldIcon color={bold ? "primary" : "inherit"} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Italic" placement="top" arrow>
                <IconButton disabled={EditorDisable} onClick={() => handleStyleChange("italic")} size="small">
                  <FormatItalicIcon color={italic ? "primary" : "inherit"} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Underline" placement="top" arrow>
                <IconButton disabled={EditorDisable} onClick={() => handleStyleChange("underline")} size="small">
                  <FormatUnderlinedIcon color={underline ? "primary" : "inherit"} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Left Align" placement="top" arrow>
                <IconButton disabled={EditorDisable} onClick={() => handleStyleChange("justifyLeft")} size="small">
                  <FormatAlignLeftIcon color={justifyLeft ? "primary" : "inherit"} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Center Align" placement="top" arrow>
                <IconButton disabled={EditorDisable} onClick={() => handleStyleChange("justifyCenter")} size="small">
                  <FormatAlignCenterIcon color={justifyCenter ? "primary" : "inherit"} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Right Align" placement="top" arrow>
                <IconButton disabled={EditorDisable} onClick={() => handleStyleChange("justifyRight")} size="small">
                  <FormatAlignRightIcon color={justifyRight ? "primary" : "inherit"} />
                </IconButton>
              </Tooltip>
              <Box sx={{ borderLeft: 1, display: "flex", borderColor: "grey.500", ml: 1, pl: 1 }}>
                <Tooltip title="Bullet List" placement="top" arrow>
                  <IconButton disabled={EditorDisable} onClick={() => handleStyleChange("insertUnorderedList")} size="small">
                    <FormatListBulletedIcon color={bulletList ? "primary" : "inherit"} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Numbered List" placement="top" arrow>
                  <IconButton disabled={EditorDisable} onClick={() => handleStyleChange("insertOrderedList")} size="small">
                    <FormatListNumberedIcon color={orderedList ? "primary" : "inherit"} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box sx={{ borderLeft: 1, display: "flex", borderColor: "grey.500", ml: 1, pl: 1 }}>
                <Tooltip title="Insert Table" placement="top" arrow>
                  <IconButton disabled={EditorDisable} onClick={(e) => setTableMenuAnchor(e.currentTarget)} size="small">
                    <TableChartIcon color="inherit" />
                  </IconButton>
                </Tooltip>
              </Box>
            </ButtonGroup>
          </Box>
        </Box>
        <Tooltip title="Mic" placement="top" arrow>
          <IconButton disabled={EditorDisable} onClick={() => { clickKeyRef.current = true; setListenPopUp(true); }} size="small" sx={{ visibility: EditorDisable ? "hidden" : "visible" }}>
            <MicIcon sx={{ color: "inherit", cursor: "pointer" }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Menu anchorEl={tableMenuAnchor} open={Boolean(tableMenuAnchor)} onClose={() => setTableMenuAnchor(null)} PaperProps={{ style: { padding: 16, minWidth: 200 } }}>
        <MenuItem disabled><Typography variant="subtitle1" fontWeight="bold">Insert Table</Typography></MenuItem>
        <MenuItem>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6}><Typography variant="body2">Rows:</Typography></Grid>
            <Grid item xs={6}>
              <TextField type="number" size="small" value={tableRows} onChange={(e) => setTableRows(Math.max(1, Math.min(10, parseInt(e.target.value, 10) || 1)))} inputProps={{ min: 1, max: 10 }} />
            </Grid>
          </Grid>
        </MenuItem>
        <MenuItem>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6}><Typography variant="body2">Columns:</Typography></Grid>
            <Grid item xs={6}>
              <TextField type="number" size="small" value={tableCols} onChange={(e) => setTableCols(Math.max(1, Math.min(8, parseInt(e.target.value, 10) || 1)))} inputProps={{ min: 1, max: 8 }} />
            </Grid>
          </Grid>
        </MenuItem>
        <MenuItem onClick={handleTableInsert}>
          <Button variant="contained" fullWidth>Insert Table ({tableRows}x{tableCols})</Button>
        </MenuItem>
      </Menu>

      <Box component="div" borderBottom="1px solid lightgray" sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        <Box
          ref={editorRef}
          component="div"
          contentEditable={!EditorDisable}
          className="editor"
          onMouseUp={getTextStylesAtCursor}
          onSelect={getTextStylesAtCursor}
          onPaste={handlePaste}
          onKeyDown={addSpaceOnTab ? handleKeyDown : undefined}
          onInput={triggerContentCheck}
          sx={{
            flex: 1,
            height: EditorHeight,
            minHeight: 120,
            overflow: "scroll",
            outline: "none",
            padding: "8px",
            "& ul, & ol": { margin: 0, paddingLeft: "24px" },
            "& li": { marginBottom: "2px" },
            "& table": { borderCollapse: "collapse", width: "100%", margin: "10px 0", border: "1px solid #ccc" },
            "& td": { border: "1px solid #ccc", padding: "8px", minWidth: "60px", verticalAlign: "top" },
            "& th": { border: "1px solid #ccc", padding: "8px", backgroundColor: "#f5f5f5", fontWeight: "bold", textAlign: "left" },
          }}
        />
      </Box>

      {FromTestReport ? (
        !viewOnly ? (
        <Box display="flex" justifyContent="space-between" alignItems="center" flexShrink={0} sx={{ margin: "0.3vh", minHeight: 48, paddingBottom: "0.5vh" }}>
          <Box sx={{ marginLeft: "0.5vw", display: "flex", alignItems: "center", gap: "0.5vw" }}>
            <input type="file" accept="image/*" ref={uploadPDFRef} id="newToolsFieldPdfInput" style={{ display: "none" }} onChange={onUploadPDF} multiple />
            <Box>
              <Button size="small" variant="contained" disabled={disableBtn} onClick={() => uploadPDFRef.current?.click()}>Upload Image</Button>
              <Button size="small" disabled={disableBtn} onClick={() => setImgCapClked(true)} sx={{ ml: 0.5 }} title="Capture from camera">
                <Box component="img" src={capturedImg ? (captureImgOffSrc?.default ?? captureImgOffSrc) : (captureImgSrc?.default ?? captureImgSrc)} alt="Capture" className="eMed_imgCap_btn" sx={{ display: "block", width: 24, height: 24 }} />
              </Button>
            </Box>
          </Box>
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <Button className="eMed_imgCap_btn" disabled={isCompleted} variant="outlined" sx={{ textTransform: "capitalize", height: "2vw", minHeight: 32, mr: "1vw" }} onClick={() => { if (editorRef.current) editorRef.current.innerHTML = ""; setPdfUploadList([]); setCapturedImg(null); }}>Clear</Button>
            <Box display="flex" alignItems="center" gap={0.5}>
              {!showOnlyApproveButton && (
                <Button disabled={isCompleted} variant="contained" sx={{ textTransform: "capitalize", height: "2vw", minHeight: 32 }} onClick={() => handleSaveResult?.(removeEmptyStyleTagsAndReplaceSpan(editorRef.current?.innerHTML ?? ""), true, false, pdfUploadList, capturedImg)}>Save</Button>
              )}
              <Button disabled={!canComplete} variant="contained" sx={{ textTransform: "capitalize", height: "2vw", minHeight: 32 }} onClick={() => handlePostResult?.(removeEmptyStyleTagsAndReplaceSpan(editorRef.current?.innerHTML ?? ""), false, true, pdfUploadList, capturedImg)}>{postButtonLabel}</Button>
            </Box>
          </Box>
        </Box>
        ) : null
      ) : (
        <Box display="flex" justifyContent="flex-end" alignItems="center" height="8vh" sx={{ margin: "0.3vh" }}>
          <Button variant="contained" sx={{ textTransform: "capitalize", height: "2vw", mr: "1vw" }} disabled={EditorDisable} onClick={() => handlePostResult?.(removeEmptyStyleTagsAndReplaceSpan(editorRef.current?.innerHTML ?? ""))}>Complete</Button>
        </Box>
      )}

      {pdfUploadList?.length > 0 ? (
        <Box sx={{ display: "flex", flexDirection: "column", maxHeight: 150, overflowY: "auto", padding: "5px", flexShrink: 0 }}>
          {pdfUploadList.map((fileDetails, index) => (
            <Box key={index} display="flex" alignItems="center" justifyContent="space-between" sx={{ minWidth: 0, padding: "2px 0" }}>
              <Typography color="#04B7B1" sx={{ flexShrink: 1, minWidth: 0, overflow: "hidden" }}>
                📄 {fileDetails?.document_name?.length > 23 ? fileDetails.document_name.slice(0, 20) + "..." : fileDetails?.document_name ?? "-"}
              </Typography>
              {(isCompleted || isDispatchshown || EditorDisable) ? null : (
                <IconButton size="small" onClick={() => setPdfUploadList((prev) => prev.filter((_, i) => i !== index))} sx={{ padding: "4px", marginRight: "5px" }}>✕</IconButton>
              )}
            </Box>
          ))}
        </Box>
      ) : capturedImg ? (
        <Box display="flex" justifyContent="center" alignItems="center" gap={0.5} flexShrink={0} sx={{ padding: "5px" }}>
          <Typography color="#04B7B1">📄 {typeof capturedImg === "string" ? "result_document.jpg" : (capturedImg?.document_name ?? "result_document.jpg")}</Typography>
          {(isCompleted || isDispatchshown || EditorDisable) ? null : <IconButton size="small" onClick={() => setCapturedImg(null)}>✕</IconButton>}
        </Box>
      ) : null}

      {imgCapClked && (
        <OpenWebCamPopUP
          capturedImg={typeof capturedImg === "string" ? capturedImg : null}
          closePopup={closeWebCamPop}
          addImgCapture={addImgCapture}
        />
      )}

      {listenPopUp && (
        <VoiceToText voiceTextConverter={voiceToTextgetter} listenPopUp={listenPopUpClose} micTextEditDisabled={micTextEditDisabled} />
      )}
    </Paper>
  );
}
